
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

// Define validation schema
const requestSchema = z.object({
  requestEmail: z.string().email({ message: "Please enter a valid email address" }),
});

type RequestFormValues = z.infer<typeof requestSchema>;

interface InviteRequestFormProps {
  onSwitchToLogin: () => void;
}

const InviteRequestForm: React.FC<InviteRequestFormProps> = ({
  onSwitchToLogin
}) => {
  const [isLoading, setIsLoading] = useState(false);

  // Initialize react-hook-form
  const form = useForm<RequestFormValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      requestEmail: '',
    },
  });

  const handleSubmit = async (values: RequestFormValues) => {
    setIsLoading(true);
    
    try {
      // In a real app, this would send the invite request to the server
      // For now, we'll simulate a successful request
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast.success("Invite request submitted! We'll notify you when an invite is available.");
      onSwitchToLogin();
    } catch (error) {
      console.error('Error requesting invite:', error);
      toast.error('Failed to submit invite request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-display font-semibold text-hormony-charcoal">
          Request an Invite
        </h1>
        <p className="mt-2 text-hormony-charcoal/70">
          Enter your email to join our waitlist. We'll send you an invite as soon as a spot becomes available.
        </p>
      </div>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="requestEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-hormony-charcoal">
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="Enter your email"
                    className="appearance-none rounded-lg relative block w-full"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <div>
            <Button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center"
            >
              {isLoading ? (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              Request Invite
            </Button>
          </div>
          
          <div className="text-center mt-4">
            <button
              type="button"
              className="text-sm text-hormony-charcoal/70 hover:text-hormony-coral transition-colors"
              onClick={onSwitchToLogin}
            >
              Back to Sign In
            </button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default InviteRequestForm;
